// ============================================================================
// @gs/feedback/client — contenu des PJ a travers l'app (CHORE-FBK-PJ-PROXY)
// ============================================================================
// DECISION-2026-06-12-PJ-PROXY-APP : plus AUCUN SAS expose au navigateur — les
// URLs `url`/`downloadUrl` du DTO sont des endpoints de l'app (Bearer requis).
// Un `<img src>` / `window.open` nu n'envoie pas le token : on materialise des
// OBJECT URLs via fetch authentifie (@gs/api-client).
//
// CACHE DE PAGE deliberement sans revoke : un object URL n'expire jamais (fini
// les vignettes cassees apres 60 min — flag retro PJ-PREVIEW), vignette et
// lightbox partagent le meme blob (1 seul fetch), et le volume est borne
// (max 3 PJ/parent, 10 Mo/PJ) — libere au dechargement de la page.

import { useEffect, useState } from 'react';
import { fetchAuthenticatedBlobObjectUrl } from '@gs/api-client';

const objectUrlCache = new Map<string, Promise<string | undefined>>();

/** Object URL (cache de page) du contenu d'une PJ — undefined si refus/erreur. */
export function getAttachmentObjectUrl(path: string): Promise<string | undefined> {
  let pending = objectUrlCache.get(path);
  if (!pending) {
    pending = fetchAuthenticatedBlobObjectUrl(path).then((u) => {
      // Echec (401 transitoire, reseau) : on ne met PAS en cache — re-tentable.
      if (!u) objectUrlCache.delete(path);
      return u;
    });
    objectUrlCache.set(path, pending);
  }
  return pending;
}

/** Hook image/lightbox : resout l'object URL (undefined pendant le chargement). */
export function useAttachmentObjectUrl(path: string | null): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    setUrl(undefined);
    if (!path) return undefined;
    void getAttachmentObjectUrl(path).then((u) => { if (alive) setUrl(u); });
    return () => { alive = false; };
  }, [path]);
  return url;
}

/**
 * Ouvre une PJ previsualisable (PDF) dans un nouvel onglet : la fenetre est
 * ouverte SYNCHRONE au clic (anti popup-block) sur about:blank — JAMAIS sur
 * l'URL d'app nue (une navigation n'emporte pas le Bearer -> JSON 401 dans
 * l'onglet, dogfood Marc 2026-06-12) — puis pointee sur l'object URL.
 * Retourne false si le contenu n'a pas pu etre charge (l'appelant toaste).
 */
export async function openAttachmentInTab(path: string): Promise<boolean> {
  const tab = window.open('about:blank', '_blank');
  const u = await getAttachmentObjectUrl(path);
  if (u && tab) {
    tab.location.href = u;
    return true;
  }
  tab?.close(); // refus/erreur -> pas d'onglet mort (ni JSON brut)
  return u !== undefined; // popup bloquee avec contenu OK = pas une erreur de chargement
}

/**
 * Telecharge une PJ (tous types) sous son filename d'origine : fetch authentifie
 * DEDIE (hors cache de page — le blob est revoque sitot le clic emis, pattern
 * prompt P2 ; le cache sans revoke reste reserve aux vignettes/lightbox), ancre
 * `<a download>` synthetique, puis URL.revokeObjectURL. JAMAIS de navigation
 * vers l'URL d'app nue. Retourne false si echec (l'appelant toaste).
 */
export async function downloadAttachment(path: string, filename: string): Promise<boolean> {
  const u = await fetchAuthenticatedBlobObjectUrl(path);
  if (!u) return false;
  const anchor = document.createElement('a');
  anchor.href = u;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(u);
  return true;
}
